import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, FileBarChart2, Download, Plus, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { handleApiError } from "@/lib/errors";

export default function Reports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/reports");
      setItems(data);
      return data;
    } catch (err) {
      toast.error("Couldn't load reports", { description: formatApiError(err) });
      return null;
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Poll the list every 3s while any report is still queued.
  useEffect(() => {
    const anyQueued = items.some((r) => r.status === "queued");
    if (!anyQueued) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const next = await load(true);
      const stillQueued = (next || []).some((r) => r.status === "queued");
      if (!stillQueued && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 3000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items]);

  const download = async (r) => {
    if (r.status !== "ready") return;
    try {
      const res = await api.get(`/reports/${r.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${r.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed", { description: formatApiError(err) });
    }
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Reports</p>
          <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">Audit-ready PDF reports</h1>
          <p className="mt-1.5 text-sm text-slate-600">Generate, share and archive professional compliance reports.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="reports-new">
              <Plus className="h-4 w-4 mr-2" /> Generate report
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <GenerateForm onCreated={() => { setOpen(false); load(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Scope</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Scans</th>
              <th className="px-5 py-3">Issues</th>
              <th className="px-5 py-3">Generated</th>
              <th className="px-5 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">
                No reports yet. Generate your first one.
              </td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`report-${r.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{r.name}</td>
                <td className="px-5 py-3 capitalize text-slate-600">{r.scope.replaceAll("_", " ")}</td>
                <td className="px-5 py-3">
                  <StatusPill status={r.status} error={r.error} />
                </td>
                <td className="px-5 py-3 text-slate-600">{r.status === "ready" ? r.scan_count : "—"}</td>
                <td className="px-5 py-3 text-slate-600">{r.status === "ready" ? r.issue_count : "—"}</td>
                <td className="px-5 py-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-right">
                  <Button
                    size="sm"
                    onClick={() => download(r)}
                    disabled={r.status !== "ready"}
                    className="rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`report-download-${r.id}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status, error }) {
  if (status === "queued") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold text-blue-700 bg-blue-50" data-testid="report-status-queued">
        <Loader2 className="h-3 w-3 animate-spin" /> Generating
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span title={error || ""} className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold text-rose-700 bg-rose-50" data-testid="report-status-failed">
        <XCircle className="h-3 w-3" /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50" data-testid="report-status-ready">
      Ready
    </span>
  );
}

function GenerateForm({ onCreated }) {
  const navigate = useNavigate();
  const [name, setName] = useState("Compliance Audit — " + new Date().toLocaleDateString());
  const [scope, setScope] = useState("open_issues");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reports", { name, scope });
      toast.success("Report queued", { description: "Generating PDF — it'll appear ready in a few seconds." });
      onCreated();
    } catch (err) {
      handleApiError(err, "Couldn't generate report", navigate);
    }
    finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle>Generate audit-ready report</DialogTitle>
        <DialogDescription>
          Pick what to include — your team will get a styled PDF with findings, scans, and an audit trail.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label>Report name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-10" data-testid="report-name" />
        </div>
        <div>
          <Label>Scope</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="mt-1 h-10" data-testid="report-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open_issues">Open issues only</SelectItem>
              <SelectItem value="recent_scans">20 most recent scans</SelectItem>
              <SelectItem value="all">Everything</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter className="mt-2">
        <Button onClick={submit} disabled={busy} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="report-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileBarChart2 className="h-4 w-4 mr-2" />Generate</>}
        </Button>
      </DialogFooter>
    </>
  );
}
